"use client"

import { Cta, SanityCtaProps } from "@/components/cta"
import { RichText, RichTextValue } from "@/components/rich-text"
import { useCallback, useEffect, useRef, useState } from "react"
import { cn } from "@/utils/cn"
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion"
import { SlideOnView } from "@/components/slide-on-view"
import { Cursor } from "@/components/cursor"
import useEmblaCarousel from "embla-carousel-react"

interface SolutionsProps {
  content: {
    title: RichTextValue
    cards: {
      _key: string
      badge: string
      title: string
      subtitle: string
      description: string
      infos?: {
        title: RichTextValue
        description: string
      }[]
      cta: SanityCtaProps
    }[]
  }
}

export function Solutions({ content }: SolutionsProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel()
  const [showCursor, setShowCursor] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  const isWheelScrolling = useRef(false)
  const wheelTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (!emblaApi) return
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return
      e.preventDefault()

      if (wheelTimeout.current) clearTimeout(wheelTimeout.current)
      wheelTimeout.current = setTimeout(() => {
        isWheelScrolling.current = false
      }, 500)

      if (isWheelScrolling.current) return
      isWheelScrolling.current = true

      if (e.deltaX > 0) {
        emblaApi.scrollNext()
      } else {
        emblaApi.scrollPrev()
      }
    },
    [emblaApi]
  )

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on("select", onSelect)
    emblaApi.on("reInit", onSelect)
    return () => {
      emblaApi.off("select", onSelect)
      emblaApi.off("reInit", onSelect)
    }
  }, [emblaApi, onSelect])

  return (
    <div className="bg-neutral-light py-10 select-none md:py-20 xl:py-32">
      <div className="mx-auto max-w-[90rem] px-6">
        <SlideOnView>
          <RichText value={content.title} />
        </SlideOnView>
      </div>
      <Cursor color="green" label="SLIDE ME!" show={showCursor} />
      <div
        onMouseEnter={() => setShowCursor(true)}
        onMouseLeave={() => setShowCursor(false)}
        onWheel={handleWheel}
        ref={emblaRef}
        className="touch-pan-x touch-auto overflow-hidden"
      >
        <div className="mx-6 flex items-center gap-4 py-5 md:mx-16 md:gap-8 lg:mx-20 xl:mx-35">
          {content.cards.map((card, index) => (
            <SolutionsCard
              key={card._key}
              card={card}
              isActive={index === selectedIndex}
            />
          ))}
        </div>
      </div>
      <div className="mt-6 flex items-center justify-center gap-2.5 md:hidden">
        {content.cards.map((card, index) => (
          <div
            key={card._key}
            role="button"
            tabIndex={0}
            aria-label={`Go to slide ${index + 1}`}
            onClick={() => emblaApi?.scrollTo(index)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") emblaApi?.scrollTo(index)
            }}
            className={cn(
              "h-2.5 shrink-0 cursor-pointer rounded-full transition-all duration-300",
              index === selectedIndex
                ? "w-7 bg-primary-500"
                : "w-2.5 bg-viola-200"
            )}
          />
        ))}
      </div>
    </div>
  )
}

function SolutionsCard({
  card,
  isActive,
}: {
  card: SolutionsProps["content"]["cards"][number]
  isActive: boolean
}) {
  const cardRef = useRef<HTMLDivElement>(null)
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  const springX = useSpring(mouseX, { stiffness: 150, damping: 15 })
  const springY = useSpring(mouseY, { stiffness: 150, damping: 15 })

  const rotateX = useTransform(springY, [-1.5, 1.5], [15, -15])
  const rotateY = useTransform(springX, [-0.5, 0.5], [-15, 15])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!cardRef.current) return
      const rect = cardRef.current.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      const x = (e.clientX - centerX) / rect.width
      const y = (e.clientY - centerY) / rect.height
      mouseX.set(x)
      mouseY.set(y)
    }
    const handleMouseEnter = () => {
      document.addEventListener("mousemove", handleMouseMove)
    }
    const handleMouseLeave = () => {
      document.removeEventListener("mousemove", handleMouseMove)
      mouseX.set(0)
      mouseY.set(0)
    }
    if (cardRef.current) {
      cardRef.current.addEventListener("mouseenter", handleMouseEnter)
      cardRef.current.addEventListener("mouseleave", handleMouseLeave)
    }
    return () => {
      if (cardRef.current) {
        cardRef.current.removeEventListener("mouseenter", handleMouseEnter)
        cardRef.current.removeEventListener("mouseleave", handleMouseLeave)
      }
      document.removeEventListener("mousemove", handleMouseMove)
    }
  }, [mouseX, mouseY])

  return (
    <motion.div
      ref={cardRef}
      className="w-max min-w-0 shrink-0 grow-0 basis-[85%] md:basis-3/5 lg:basis-3/5 min-[1920px]:basis-[38.46%]"
      style={{
        rotateX,
        rotateY,
        transformStyle: "preserve-3d",
        transformPerspective: 1000,
      }}
    >
      <div
        className={cn(
          "relative mx-auto flex w-full max-w-[114rem] flex-col rounded-xl p-6 shadow-lg transition-all duration-700 md:rounded-2xl md:px-20 md:py-12",
          "transition-colors delay-200 duration-300",
          isActive
            ? "overflow-hidden border border-primary-500 bg-primary-500 text-neutral-light"
            : "overflow-hidden border border-neutral-dark bg-neutral-light text-viola-600"
        )}
      >
        <div className="w-max rounded-3xl border-2 border-current px-8 py-3 font-family-gtpressura text-lg leading-none md:px-12 md:text-xl">
          {card.badge}
        </div>
        <h3 className="mt-6 text-xl font-bold md:text-5xl xl:text-6xl">
          {card.title}
        </h3>
        <p className="mt-1 text-xl font-bold md:text-2xl">{card.subtitle}</p>
        <p className="mt-4 max-w-[55rem] font-family-gtpressura font-light md:text-xlg">
          {card.description}
        </p>
        {card.infos && (
          <div className="mt-7.5 hidden justify-between md:flex">
            {card.infos.map((info, i) => (
              <div key={i} className="flex-1">
                <RichText value={info.title} />
                <p className="font-family-gthaptik text-lg font-light xl:text-xlg">
                  {info.description}
                </p>
              </div>
            ))}
          </div>
        )}
        {card.cta?.label && (
          <div className="mt-14">
            <Cta sanity={card.cta} />
          </div>
        )}
      </div>
    </motion.div>
  )
}
